// Update the DeleteProjectModal component

import React, { useState } from 'react';
import { X, AlertTriangle, Trash2, Crown, Shield } from 'lucide-react';

const DeleteProjectModal = ({ show, onClose, onConfirm, project, currentUser }) => {
    const [confirmText, setConfirmText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    if (!show || !project) return null;

    const isOwner = project.owner === currentUser._id || project.owner?._id === currentUser._id;
    const isAdmin = currentUser.role === 'admin' || currentUser.role === 'super_admin';

    const getOwnerDisplayName = () => {
        if (typeof project.owner === 'string') {
            return 'Project Owner'; // If we only have ID
        }
        if (project.owner?.firstName && project.owner?.lastName) {
            return `${project.owner.firstName} ${project.owner.lastName}`;
        }
        return project.owner?.email || 'Unknown Owner';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (confirmText !== 'DELETE') {
            alert('You must type "DELETE" exactly to confirm deletion.');
            return;
        }

        setIsDeleting(true);
        try {
            await onConfirm(project._id, project.name);
            setConfirmText('');
            onClose();
        } catch (error) {
            console.error('Error in delete confirmation:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleClose = () => {
        if (!isDeleting) {
            setConfirmText('');
            onClose();
        }
    };

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal-content delete-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header danger">
                    <div className="modal-title-section">
                        <AlertTriangle className="modal-icon danger" />
                        <h2>Delete Project</h2>
                        {!isOwner && isAdmin && (
                            <div className="admin-badge">
                                <Shield style={{ width: '14px', height: '14px' }} />
                                Admin Action
                            </div>
                        )}
                    </div>
                    <button
                        className="modal-close-btn"
                        onClick={handleClose}
                        disabled={isDeleting}
                    >
                        <X />
                    </button>
                </div>

                <div className="modal-body">
                    <div className="delete-warning">
                        <div className="warning-content">
                            <h3>⚠️ This action cannot be undone!</h3>
                            <p>You are about to permanently delete:</p>

                            <div className="project-info">
                                <div className="project-header">
                                    <strong>"{project.name}"</strong>
                                    <span className="project-code">Code: {project.code}</span>
                                </div>

                                <div className="ownership-info">
                                    <div className="owner-section">
                                        <Crown style={{ width: '16px', height: '16px', color: '#f59e0b' }} />
                                        <span>Owner: {getOwnerDisplayName()}</span>
                                        {isOwner && <span className="you-label">(You)</span>}
                                    </div>

                                    {project.members && project.members.length > 0 && (
                                        <div className="members-count">
                                            <span>{project.members.length} team member{project.members.length !== 1 ? 's' : ''} will lose access</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {!isOwner && isAdmin && (
                                <div className="admin-warning">
                                    <Shield style={{ width: '20px', height: '20px' }} />
                                    <div>
                                        <strong>Administrator Override</strong>
                                        <p>You are deleting a project you don't own. This action will be logged and the owner will be notified.</p>
                                    </div>
                                </div>
                            )}

                            <div className="consequences-list">
                                <h4>This will permanently remove:</h4>
                                <ul>
                                    <li>All project data and settings</li>
                                    <li>All tasks, columns, and workflows</li>
                                    <li>All uploaded files and attachments</li>
                                    <li>All team member access and permissions</li>
                                    <li>All project activity history</li>
                                    {project.members && project.members.length > 0 && (
                                        <li>Access for {project.members.length} team member{project.members.length !== 1 ? 's' : ''}</li>
                                    )}
                                </ul>
                            </div>

                            <div className="confirmation-input">
                                <label htmlFor="confirmText">
                                    To confirm deletion, type <strong>DELETE</strong> in the field below:
                                </label>
                                <input
                                    id="confirmText"
                                    type="text"
                                    value={confirmText}
                                    onChange={(e) => setConfirmText(e.target.value)}
                                    placeholder="Type DELETE here"
                                    className="confirm-input"
                                    disabled={isDeleting}
                                    autoComplete="off"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="modal-actions">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleClose}
                        disabled={isDeleting}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-danger"
                        onClick={handleSubmit}
                        disabled={confirmText !== 'DELETE' || isDeleting}
                    >
                        {isDeleting ? (
                            <>
                                <div className="spinner"></div>
                                Deleting...
                            </>
                        ) : (
                            <>
                                <Trash2 style={{ width: '16px', height: '16px' }} />
                                {!isOwner && isAdmin ? 'Delete as Admin' : 'Delete Project'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DeleteProjectModal;